import { ReactNode, useMemo } from 'react';
import { MessageDescriptor } from 'react-intl';
import { matchPath, useLocation } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { useScreenSize } from 'hooks/device/useScreenSize';
import { Link, SidebarLayout } from 'routing/SidebarLayout';

type LinkDeclaration = Omit<Link, 'id' | 'activeLink' | 'nested'>;

/**
 * A set of link entries where identifiers would uniquely identify a route accessible through a link.
 */
export type LinkSet<ROUTE_ID extends string = string> = {
  [key in ROUTE_ID]: LinkDeclaration;
};
interface Props<LINK_ID extends string> {
  linkIds: LINK_ID[];
  links: LinkSet<LINK_ID>;
  children: ReactNode;
  pageTitle?: MessageDescriptor;
  startWithOpenedMenu?: boolean;
}

const linkFactory = <ROUTE_ID extends string>(
  id: ROUTE_ID,
  declaration: LinkDeclaration
): Link<ROUTE_ID> => {
  const { label, icon, to } = declaration;
  return {
    ...declaration,
    id,
    label,
    icon,
    to,
  };
};

export const RoutedSidebarMenu = <LINK_ID extends string>({
  linkIds,
  links,
  children,
  pageTitle,
  startWithOpenedMenu = false,
}: Props<LINK_ID>) => {
  const location = useLocation();
  const { up: isGreaterThanTablet } = useScreenSize('tablet');

  const sideLinks = useMemo<Link<LINK_ID>[]>(
    () => linkIds.map(id => linkFactory(id, links[id])),
    [linkIds, links]
  );
  const activeLink = useMemo(() => {
    const link = sideLinks.find(l =>
      matchPath(catchAll(l.to!), location.pathname)
    );
    if (link) return link;
    if (!isGreaterThanTablet) {
      return undefined;
    }
    return sideLinks[0];
  }, [isGreaterThanTablet, location.pathname, sideLinks]);

  return (
    <SidebarLayout
      activeLink={activeLink?.id}
      links={sideLinks}
      pageTitle={pageTitle}
      startWithOpenedMenu={startWithOpenedMenu}
    >
      {children}
    </SidebarLayout>
  );
};
